// FormaPagamentoView — gerencia cartões salvos + assinaturas recorrentes.
//
// Estado dividido em duas seções:
//   1) Meus cartões — cartão primário (priority=0) + cartão reserva opcional
//      (priority=1). Reserva = fallback que entra automaticamente se o
//      principal falhar. Tanto adicionar quanto promover/remover.
//   2) Minhas assinaturas — pacotes recorrentes (Pix mensal ou CC) com
//      próximas cobranças, status e histórico das últimas tentativas.
//
// Apenas exibe quando ccEnabled na config global. Senão, mostra só Pix info.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowUpCircle,
  CheckCircle2,
  CreditCard,
  Loader2,
  Pause,
  Play,
  Plus,
  QrCode,
  Trash2,
  XCircle
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  BillingAddressFields,
  emptyBillingAddress,
  validateBillingAddress as validateAddrClient,
  type BillingAddress
} from "@app/ui/components/billing-address-fields";
import {
  api,
  ApiError,
  type PaymentMethodRow,
  type SubscriptionRow
} from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";

const phase = signal<"loading" | "ready" | "error">("loading");
const methods = signal<PaymentMethodRow[]>([]);
const subscriptions = signal<SubscriptionRow[]>([]);
const errMsg = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [m, s] = await Promise.all([
      api.listPaymentMethods(),
      api.listSubscriptions()
    ]);
    methods.value = m.items;
    subscriptions.value = s.items;
    phase.value = "ready";
  } catch (e) {
    errMsg.value = e instanceof Error ? e.message : String(e);
    phase.value = "error";
  }
}

export function FormaPagamentoView({ embedded = false }: { embedded?: boolean } = {}) {
  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-3">
        <Skeleton class="h-32 w-full" />
        <Skeleton class="h-32 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  return (
    <div class="space-y-6">
      {!embedded && (
        <header>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <CreditCard className="size-6 text-primary" />
            Forma de pagamento
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Cadastre cartões pra cobrança automática mensal, ou continue pagando
            via Pix avulso. Você sempre pode trocar.
          </p>
        </header>
      )}

      <CardsSection />

      <SubscriptionsSection />
    </div>
  );
}

function CardsSection() {
  const list = methods.value;
  const primary = list.find((m) => m.priority === 0);
  const backup = list.find((m) => m.priority === 1);

  return (
    <section class="space-y-3">
      <h2 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
        Meus cartões
      </h2>

      <div class="grid gap-3 md:grid-cols-2">
        <CardSlot
          label="Cartão principal"
          tag="Cobrança automática usa este"
          method={primary}
          slotPriority={0}
        />
        <CardSlot
          label="Cartão reserva"
          tag="Usado só se o principal falhar"
          method={backup}
          slotPriority={1}
        />
      </div>

      {!primary && !backup && (
        <p class="text-xs text-muted-foreground">
          Você ainda não cadastrou nenhum cartão. Sem cartão, suas
          assinaturas continuam funcionando via Pix mensal (você recebe um
          QR novo todo mês).
        </p>
      )}
    </section>
  );
}

function CardSlot({
  label,
  tag,
  method,
  slotPriority
}: {
  label: string;
  tag: string;
  method: PaymentMethodRow | undefined;
  slotPriority: 0 | 1;
}) {
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  async function remove() {
    if (!method) return;
    const ok = await openConfirm({
      title: "Remover cartão?",
      message: `${method.brand} •••• ${method.last4} será removido. Você pode adicionar de novo a qualquer momento.`,
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.removePaymentMethod(method.id);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function promote() {
    if (!method || slotPriority === 0) return;
    setBusy(true);
    try {
      await api.promotePaymentMethod(method.id, "primary");
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (adding) {
    return (
      <AddCardForm
        slotPriority={slotPriority}
        onDone={() => {
          setAdding(false);
          void load();
        }}
        onCancel={() => setAdding(false)}
      />
    );
  }

  if (!method) {
    return (
      <div class="rounded-md border border-dashed border-border bg-card/40 p-4">
        <div class="mb-1 text-sm font-medium">{label}</div>
        <p class="mb-3 text-xs text-muted-foreground">{tag}</p>
        <button
          type="button"
          onClick={() => setAdding(true)}
          class="inline-flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10"
        >
          <Plus className="size-4" />
          Adicionar cartão
        </button>
      </div>
    );
  }

  return (
    <div class="rounded-md border border-border bg-card p-4">
      <div class="mb-3 flex items-start gap-3">
        <div class="grid size-10 place-items-center rounded-md bg-violet-500/15 text-violet-600">
          <CreditCard className="size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="text-sm font-medium">{label}</div>
          <div class="text-xs text-muted-foreground">{tag}</div>
        </div>
      </div>
      <div class="mb-3 rounded-md border border-border bg-background/60 px-3 py-2 font-mono text-sm">
        {method.brand} •••• {method.last4}
        <span class="ml-2 text-xs text-muted-foreground">
          {String(method.expMonth ?? "").padStart(2, "0")}/{method.expYear}
        </span>
      </div>
      {method.holderName && (
        <p class="mb-3 text-xs text-muted-foreground">{method.holderName}</p>
      )}
      <div class="flex flex-wrap items-center gap-2">
        {slotPriority === 1 && (
          <button
            type="button"
            onClick={promote}
            disabled={busy}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary hover:text-primary disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <ArrowUpCircle className="size-3" />
            )}
            Tornar principal
          </button>
        )}
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-destructive hover:text-destructive disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Trash2 className="size-3" />
          )}
          Remover
        </button>
      </div>
    </div>
  );
}

function AddCardForm({
  slotPriority,
  onDone,
  onCancel
}: {
  slotPriority: 0 | 1;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [number, setNumber] = useState("");
  const [holderName, setHolderName] = useState("");
  const [holderDoc, setHolderDoc] = useState("");
  const [cvv, setCvv] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [address, setAddress] = useState<BillingAddress>(emptyBillingAddress);
  const [addrError, setAddrError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    const addrErr = validateAddrClient(address);
    if (addrErr) {
      setAddrError(addrErr);
      return;
    }
    setAddrError(null);
    setSubmitting(true);
    try {
      await api.addPaymentMethod({
        number,
        holderName,
        holderDoc,
        cvv,
        expMonth: parseInt(expMonth, 10),
        expYear: parseInt(expYear, 10),
        billingAddress: address,
        asPriority: slotPriority === 1 ? "backup" : "primary"
      });
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === "cc_disabled") {
        void openAlert({
          title: "Cartão indisponível",
          message:
            "O pagamento por cartão de crédito não está ativo na plataforma agora. Use Pix por enquanto."
        });
      } else {
        void openAlert({
          title: "Erro ao adicionar cartão",
          message: e instanceof Error ? e.message : String(e)
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      class="space-y-3 rounded-md border border-primary/40 bg-primary/5 p-4"
    >
      <div class="text-sm font-medium">
        Adicionar {slotPriority === 1 ? "cartão reserva" : "cartão principal"}
      </div>
      <div class="text-[11px] text-amber-700 dark:text-amber-400">
        <AlertCircle className="mr-1 inline size-3" />
        Ambiente de teste — os dados não são enviados pra nenhum gateway real
        enquanto o admin não conectar credenciais.
      </div>
      {/* Dados do cartão */}
      <fieldset class="space-y-2">
        <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Dados do cartão
        </legend>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Número do cartão
          </label>
          <input
            type="text"
            inputMode="numeric"
            required
            minLength={12}
            maxLength={19}
            value={number}
            onInput={(e) => setNumber((e.target as HTMLInputElement).value)}
            placeholder="4111 1111 1111 1111"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={submitting}
          />
        </div>
        <div class="grid grid-cols-3 gap-2">
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Mês
            </label>
            <input
              type="number"
              min={1}
              max={12}
              required
              value={expMonth}
              onInput={(e) => setExpMonth((e.target as HTMLInputElement).value)}
              placeholder="MM"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Ano
            </label>
            <input
              type="number"
              min={2025}
              max={2099}
              required
              value={expYear}
              onInput={(e) => setExpYear((e.target as HTMLInputElement).value)}
              placeholder="AAAA"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              CVV
            </label>
            <input
              type="text"
              inputMode="numeric"
              required
              minLength={3}
              maxLength={4}
              value={cvv}
              onInput={(e) => setCvv((e.target as HTMLInputElement).value)}
              placeholder="123"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
        </div>
      </fieldset>

      {/* Dados do portador */}
      <fieldset class="space-y-2">
        <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Dados do portador
        </legend>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Nome do portador
          </label>
          <input
            type="text"
            required
            value={holderName}
            onInput={(e) => setHolderName((e.target as HTMLInputElement).value)}
            placeholder="Como está impresso no cartão"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm uppercase"
            disabled={submitting}
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            CPF do portador
          </label>
          <input
            type="text"
            inputMode="numeric"
            required
            value={holderDoc}
            onInput={(e) => setHolderDoc((e.target as HTMLInputElement).value)}
            placeholder="000.000.000-00"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            disabled={submitting}
          />
        </div>
      </fieldset>

      {/* Endereço de cobrança */}
      <fieldset class="space-y-2">
        <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Endereço de cobrança
        </legend>
        <BillingAddressFields
          value={address}
          onChange={setAddress}
          disabled={submitting}
        />
        {addrError && (
          <p class="text-[11px] text-destructive">{addrError}</p>
        )}
      </fieldset>
      <div class="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
          Salvar
        </button>
      </div>
    </form>
  );
}

function SubscriptionsSection() {
  const list = subscriptions.value;
  if (list.length === 0) {
    return (
      <section class="space-y-3">
        <h2 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Minhas assinaturas
        </h2>
        <div class="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          Você ainda não tem assinaturas recorrentes. Pacotes que você comprar
          como recorrentes vão aparecer aqui.
        </div>
      </section>
    );
  }

  return (
    <section class="space-y-3">
      <h2 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
        Minhas assinaturas
      </h2>
      <ul class="space-y-3">
        {list.map((s) => (
          <SubscriptionCard key={s.id} sub={s} />
        ))}
      </ul>
    </section>
  );
}

function SubscriptionCard({ sub }: { sub: SubscriptionRow }) {
  const [busy, setBusy] = useState(false);

  async function toggleBilling() {
    const next = sub.billingType === "credit_card" ? "pix_monthly" : "credit_card";
    if (next === "credit_card") {
      const hasCard = methods.value.some((m) => m.priority === 0);
      if (!hasCard) {
        void openAlert({
          title: "Cadastre um cartão",
          message:
            "Pra trocar pra cobrança recorrente no cartão, cadastre primeiro um cartão principal acima."
        });
        return;
      }
    }
    setBusy(true);
    try {
      await api.updateSubscription({ id: sub.id, billingType: next });
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function togglePause() {
    setBusy(true);
    try {
      await api.updateSubscription({
        id: sub.id,
        status: sub.status === "paused" ? "active" : "paused"
      });
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    const ok = await openConfirm({
      title: "Cancelar assinatura?",
      message: `A cobrança recorrente de "${sub.pacoteNomeSnap}" será cancelada. Esta ação não pode ser desfeita aqui.`,
      confirmLabel: "Cancelar",
      danger: true
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.cancelSubscription(sub.id);
      await load();
    } finally {
      setBusy(false);
    }
  }

  const isPix = sub.billingType === "pix_monthly";
  const isCanceled = sub.status === "canceled";

  return (
    <li class="rounded-md border border-border bg-card p-4">
      <div class="mb-3 flex items-start gap-3">
        <div
          class={`grid size-10 place-items-center rounded-md ${
            isPix
              ? "bg-emerald-500/15 text-emerald-600"
              : "bg-violet-500/15 text-violet-600"
          }`}
        >
          {isPix ? <QrCode className="size-5" /> : <CreditCard className="size-5" />}
        </div>
        <div class="min-w-0 flex-1">
          <div class="font-medium">{sub.pacoteNomeSnap}</div>
          <div class="text-xs text-muted-foreground">
            R$ {(sub.valorCents / 100).toFixed(2).replace(".", ",")} /mês via{" "}
            {isPix ? "Pix mensal" : "Cartão"} ·{" "}
            <StatusLabel status={sub.status} />
          </div>
        </div>
      </div>

      <div class="mb-3 grid gap-2 text-xs sm:grid-cols-3">
        <Info label="Próxima cobrança">
          {isCanceled
            ? "—"
            : new Date(sub.nextChargeAt).toLocaleDateString("pt-BR")}
        </Info>
        <Info label="Última cobrança">
          {sub.lastChargeAt
            ? new Date(sub.lastChargeAt).toLocaleDateString("pt-BR")
            : "—"}
        </Info>
        <Info label="Tentativas falhas">{sub.failedAttempts}</Info>
      </div>

      {sub.recentCharges.length > 0 && (
        <div class="mb-3 rounded-md border border-border bg-background/40 px-3 py-2">
          <div class="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            Últimas cobranças
          </div>
          <ul class="space-y-1 text-xs">
            {sub.recentCharges.slice(0, 3).map((c) => (
              <li key={c.id} class="flex items-center gap-2">
                <ChargeIcon status={c.status} />
                <span class="flex-1">
                  {new Date(c.chargedAt).toLocaleDateString("pt-BR")} — R${" "}
                  {(c.valorCents / 100).toFixed(2).replace(".", ",")}
                  {c.methodPriority !== null && (
                    <span class="ml-1 text-muted-foreground">
                      ({c.methodPriority === 0 ? "principal" : "reserva"})
                    </span>
                  )}
                </span>
                {c.failureReason && (
                  <span class="text-[10px] text-amber-600">{c.failureReason}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isCanceled && (
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={toggleBilling}
            disabled={busy}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary hover:text-primary disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="size-3 animate-spin" />
            ) : isPix ? (
              <CreditCard className="size-3" />
            ) : (
              <QrCode className="size-3" />
            )}
            Trocar pra {isPix ? "Cartão" : "Pix mensal"}
          </button>
          <button
            type="button"
            onClick={togglePause}
            disabled={busy}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-amber-500 hover:text-amber-600 disabled:opacity-50"
          >
            {sub.status === "paused" ? (
              <Play className="size-3" />
            ) : (
              <Pause className="size-3" />
            )}
            {sub.status === "paused" ? "Retomar" : "Pausar"}
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-destructive hover:text-destructive disabled:opacity-50"
          >
            <XCircle className="size-3" />
            Cancelar
          </button>
        </div>
      )}
    </li>
  );
}

function Info({ label, children }: { label: string; children: any }) {
  return (
    <div>
      <div class="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div class="font-medium tabular-nums">{children}</div>
    </div>
  );
}

function StatusLabel({ status }: { status: SubscriptionRow["status"] }) {
  if (status === "active")
    return (
      <span class="text-emerald-600">
        <CheckCircle2 className="mr-0.5 inline size-3" />
        ativa
      </span>
    );
  if (status === "paused")
    return <span class="text-amber-600">pausada</span>;
  if (status === "past_due")
    return <span class="text-destructive">em atraso</span>;
  return <span class="text-muted-foreground">cancelada</span>;
}

function ChargeIcon({ status }: { status: SubscriptionRow["recentCharges"][number]["status"] }) {
  if (status === "succeeded") return <CheckCircle2 className="size-3 text-emerald-600" />;
  if (status === "failed") return <XCircle className="size-3 text-destructive" />;
  return <Loader2 className="size-3 text-muted-foreground" />;
}

